import { ActionIcon, Stack, Table, Text } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { useFlags } from 'src/feature-flags/FlagsHook';
import PageLoader from 'src/UI/loader/PageLoader';
import { formatShortDate } from 'src/utils/dateFormatting';
import { formatFileSize } from 'src/utils/fileSizeFormatting';
import useDownloadSkillFile from './hooks/useDownloadSkillFile';
import useTenantSkillFiles from './hooks/useTenantSkillFiles';

function SkillFilesSection({ skillId }: { skillId: string }) {
  const { flagEnabledCustomSkillFiles } = useFlags();
  // The flag is undefined while LaunchDarkly initialises; only a definite true may fetch,
  // or every skill editor open would 404 against the gated route.
  const {
    data: files,
    isLoading,
    isError,
  } = useTenantSkillFiles(skillId, flagEnabledCustomSkillFiles === true);
  const { downloadFile, pendingFileId } = useDownloadSkillFile(skillId);

  const renderFiles = () => {
    if (isLoading) {
      return <PageLoader absolute={false} />;
    }
    if (isError) {
      return (
        <Text size="sm" c="red">
          The skill&apos;s files could not be loaded.
        </Text>
      );
    }
    if (!files || files.length === 0) {
      return (
        <Text size="sm" c="dimmed">
          No supporting files yet — attach them by asking Siesta while building the skill.
        </Text>
      );
    }
    return (
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Size</Table.Th>
            <Table.Th>Updated</Table.Th>
            <Table.Th aria-label="Actions" />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {files.map(file => (
            <Table.Tr key={file.id}>
              <Table.Td>{file.filename}</Table.Td>
              <Table.Td>{formatFileSize(file.sizeBytes)}</Table.Td>
              <Table.Td>{file.updatedAt ? formatShortDate(file.updatedAt) : '—'}</Table.Td>
              <Table.Td>
                <ActionIcon
                  variant="subtle"
                  aria-label={`Download ${file.filename}`}
                  loading={pendingFileId === file.id}
                  onClick={() => downloadFile(file.id, file.filename)}
                >
                  <IconDownload size={16} />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  };

  return (
    <Stack gap="xs">
      <Text fw={500} size="sm">
        Supporting files
      </Text>
      {renderFiles()}
    </Stack>
  );
}

export default SkillFilesSection;
